import { RecurrenceMode } from "../../flatbuffers/generated/archive/recurrence-mode";
import { TimedActionT } from "../../flatbuffers/generated/archive/timed-action";
import { formatUuidT } from "../../flatbuffers/display";
import { BankFloorOpening, type LiftDoorMap } from "../../utils/bankFloorOpenings";

export interface TimedActionDraft {
  id?: string;
  bank_id: string;
  organization_id: string;
  lift_id?: string;
  name: string;
  enabled: boolean;
  start_message_value: number;
  stop_message_value: number;
  /**
   * Independent active state for the start/stop command, carried on
   * `UiCmd.active` at dispatch — NOT a distinct message. The same
   * `start_message_value`/`stop_message_value` with opposite active states is
   * a legal, common configuration (e.g. CarCallLock active:true to start,
   * the SAME CarCallLock active:false to stop); the two sides resolving to
   * the exact same (value, active) pair is what LiftCloud's
   * `validateStartStopDiffer` rejects.
   */
  start_message_active: boolean;
  stop_message_active: boolean;
  position: number;
  recurrence_mode: RecurrenceMode;
  start_seconds_of_day: number;
  stop_seconds_of_day: number;
  days_of_week: number;
  timezone: string;
  stop_next_day: boolean;
  start_at?: string;
  stop_at?: string;
  week_interval?: number;
  days_of_month?: number;
  interval_seconds?: number;
  /**
   * MONTHLY_WEEKDAY mode — which occurrence of `days_of_week`'s (single)
   * weekday to fire on: 1..5 for "the Nth occurrence", ignored when
   * `month_week_last` is true.
   *
   * REQUIRED, not optional — deliberately, unlike `week_interval`/
   * `days_of_month`/`interval_seconds` above. Those are legitimately absent
   * for a mode that doesn't use them (their `!== 0` guard in
   * `mapTimedActionTToDraft` means "0 is this mode's unused value"). An
   * ordinal of 0 is never valid for ANY monthly_weekday row — it matches no
   * occurrence arithmetically — so there is no safe "absent" state to model
   * with `?:`; a row that decoded a missing value as 0 would silently never
   * fire, the same zero-sentinel bug class this codebase has hit before.
   */
  month_week_ordinal: number;
  /** True selects the LAST occurrence instead of the Nth (overrides `month_week_ordinal`). */
  month_week_last: boolean;
  valid_states: string[];
  /**
   * Full set of target banks. EMPTY means the target set is exactly
   * `{bank_id}` — the behaviour of every action written before granular
   * targeting existed.
   *
   * INVARIANT, enforced server-side: when non-empty, `bank_ids[0]` equals
   * `bank_id`. `bank_id` stays the row's owning bank (org scoping, cascade
   * delete, channel lookup), so the two must never disagree.
   */
  bank_ids?: string[];
  /**
   * Full set of target lifts. EMPTY falls back to the scalar `lift_id`; with
   * that also unset the action stays bank-wide.
   */
  lift_ids?: string[];
  /**
   * Target floors in interval notation (`"[1,5),[10,12]"`). EMPTY falls back to
   * the scalar `position`. Parsed and validated by `./floorSpec`.
   */
  floor_spec?: string;
}

export interface ControlOption {
  value: number;
  label: string;
  defaultStopValue?: number;
}

/** A selectable `{ id, name }` entry — used for bank and lift pickers. */
export interface IdNameOption {
  id: string;
  name: string;
}

/**
 * Bank + lift picker data for the editor form. The two always travel together —
 * a container either offers retargeting (the wizard) or it offers none — so they
 * are bundled into a single optional prop rather than two independent optionals.
 */
export interface ScheduledActionSelectors {
  bankOptions: IdNameOption[];
  /**
   * Lifts KEYED BY BANK, not a flat list.
   *
   * The editor targets several banks at once, so a flat list cannot work: it
   * could only ever be scoped to one bank (leaving the other selected banks'
   * cars unreachable) or hold every bank's cars at once (offering cars that
   * belong to no selected bank). Keying by bank lets the form show exactly the
   * cars of the banks currently selected, and group them so that the "Car 1"
   * of one bank is distinguishable from the "Car 1" of another — car names
   * repeat across banks constantly.
   *
   * A bank absent from this map contributes no lifts, which is how a bank whose
   * cars are still loading behaves.
   */
  liftsByBank: Record<string, IdNameOption[]>;
  /**
   * The floors each bank actually has, WITH their front/rear openings, keyed by
   * bank. Two jobs: it is the domain that rejects a floor set naming a floor the
   * targeted banks do not have, and it is what the per-floor opening grid
   * renders as checkboxes.
   *
   * Carries `BankFloorOpening` rather than bare positions because the openings
   * are the point — an operator picking floors for `RearCarCallLock` needs to
   * see which floors even HAVE a rear door, and a list of numbers cannot say.
   *
   * Keyed by bank for the same reason as `liftsByBank`: the domain is the union
   * across the SELECTED banks. A union across every selectable bank would
   * accept a floor that exists only in a bank the action does not target.
   *
   * OMITTED disables the check and falls the editor back to the Advanced
   * expression — which is the correct behaviour while the floor lists are still
   * loading, since validating against an unknown domain would reject every legal
   * spec and a grid built from it would be missing every floor.
   */
  floorsByBank?: Record<string, BankFloorOpening[]>;
  /**
   * True while the floor lists are still being fetched.
   *
   * Distinguishes "not loaded yet" from "this bank has no floors", which
   * `floorsByBank` alone cannot. Without it the editor rendered the expert
   * expression input on open and swapped it for the grid when the response
   * landed — pulling the field out from under anyone already typing in it.
   */
  floorsLoading?: boolean;
  /**
   * Each lift's own per-floor doors, keyed by bank then lift.
   *
   * The bank-level flags on `floorsByBank` only say whether ANY car in the bank
   * has an opening at a floor. Once an action names particular cars, the honest
   * answer is the union over just those cars — a bank where one car has a rear
   * door at the lobby and another does not is ordinary, and the bank-level flag
   * cannot tell them apart.
   *
   * OMITTED, or missing an entry, degrades to the bank-level flags.
   */
  liftDoorsByBank?: Record<string, Record<string, LiftDoorMap>>;
  /**
   * Why each bank that did not load failed, keyed by bank id. Lets the editor
   * name the cause instead of collapsing a permission error, a dropped
   * connection and a bank with no floor rows into one sentence.
   */
  floorLoadFailures?: Record<string, "request-failed" | "no-floors">;
}

/** Safely coerce a string | Uint8Array | null FlatBuffer string field to string. */
function coerceString(v: string | Uint8Array | null | undefined): string {
  if (v == null) return "";
  if (typeof v === "string") return v;
  // A FlatBuffer string read as raw bytes must be UTF-8 decoded; String(v) on a
  // Uint8Array yields the comma-joined byte values ("72,105,…"), not the text.
  return new TextDecoder().decode(v);
}

export function mapTimedActionTToDraft(t: TimedActionT): TimedActionDraft {
  const idStr = formatUuidT(t.id);
  const bankIdStr = formatUuidT(t.bankId);
  const orgIdStr = formatUuidT(t.organizationId);
  const liftIdStr = t.liftId != null ? formatUuidT(t.liftId) : undefined;
  const bankIds = mapUuidVector(t.bankIds);
  const liftIds = mapUuidVector(t.liftIds);
  const floorSpec = coerceString(t.floorSpec);

  return {
    ...(idStr !== "" ? { id: idStr } : {}),
    bank_id: bankIdStr,
    organization_id: orgIdStr,
    ...(liftIdStr !== undefined && liftIdStr !== "" ? { lift_id: liftIdStr } : {}),
    name: coerceString(t.name),
    enabled: t.enabled,
    start_message_value: t.startMessageValue,
    stop_message_value: t.stopMessageValue,
    start_message_active: t.startMessageActive,
    stop_message_active: t.stopMessageActive,
    position: t.position,
    recurrence_mode: t.recurrenceMode,
    start_seconds_of_day: t.startSecondsOfDay,
    stop_seconds_of_day: t.stopSecondsOfDay,
    days_of_week: t.daysOfWeek,
    timezone: coerceString(t.timezone),
    stop_next_day: t.stopNextDay,
    ...(t.startAt != null ? { start_at: coerceString(t.startAt) } : {}),
    ...(t.stopAt != null ? { stop_at: coerceString(t.stopAt) } : {}),
    ...(t.weekInterval !== 0 ? { week_interval: t.weekInterval } : {}),
    ...(t.daysOfMonth !== 0 ? { days_of_month: t.daysOfMonth } : {}),
    ...(t.intervalSeconds !== 0 ? { interval_seconds: t.intervalSeconds } : {}),
    // Unconditional, unlike the three above — see the field's own doc
    // comment on TimedActionDraft for why 0 cannot mean "unused" here.
    month_week_ordinal: t.monthWeekOrdinal,
    month_week_last: t.monthWeekLast,
    valid_states: t.validStates ?? [],
    // Empty vectors are omitted rather than stored as [], so a round-tripped
    // draft is byte-identical to one that never used granular targeting and a
    // dirty-check does not report a spurious change.
    ...(bankIds.length > 0 ? { bank_ids: bankIds } : {}),
    ...(liftIds.length > 0 ? { lift_ids: liftIds } : {}),
    ...(floorSpec !== "" ? { floor_spec: floorSpec } : {}),
  };
}

/**
 * Map a FlatBuffer Uuid vector to canonical id strings, dropping entries that do
 * not format (a nil or malformed UUID names nothing that exists).
 */
function mapUuidVector(ids: TimedActionT["bankIds"]): string[] {
  if (ids == null) return [];

  return ids.map((id) => formatUuidT(id)).filter((id) => id !== "");
}

export function emptyTimedActionDraft(
  partial: Partial<TimedActionDraft> & { bank_id: string; organization_id: string },
): TimedActionDraft {
  const defaults: TimedActionDraft = {
    bank_id: partial.bank_id,
    organization_id: partial.organization_id,
    name: "",
    enabled: true,
    start_message_value: 0,
    stop_message_value: 0,
    start_message_active: true,
    stop_message_active: true,
    position: 0,
    recurrence_mode: RecurrenceMode.ONE_SHOT,
    start_seconds_of_day: 0,
    stop_seconds_of_day: 0,
    days_of_week: 0,
    timezone: "",
    stop_next_day: false,
    month_week_ordinal: 1,
    month_week_last: false,
    valid_states: [],
  };
  return { ...defaults, ...partial };
}
